import { groupList } from '@knowledge/service/api/general';
import useCrud from 'hooks/useCrud';
import { useCallback, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

export const useLabel = () => {
  const { t } = useTranslate();

  const groupLabel = useMemo<Record<Knowledge.GroupType, string>>(() => {
    return {
      LOGISTICS: t('knowledge.发货与物流'),
      STORE: t('店铺服务'),
    };
  }, [t]);

  const knowledgeLabel = useMemo<Record<Knowledge.KnowledgeType, string>>(() => {
    return {
      LOGISTICS_SEND_OUT_TIME: t('发货天数'),
      LOGISTICS_SEND_OUT_PLACE: t('发货地'),
      LOGISTICS_DELIVERED_IN_TIME: t('运输天数'),
      LOGISTICS_SEND_PICTURES_BEFORE: t('发货前发照片'),
      LOGISTICS_DELIVERY_COURIER_TYPE: t('指定快递种类'),
      LOGISTICS_CONSOLIDATED_MULTIPLE_ORDER: t('多个订单合并发货'),
      SHOP_INVOICE: t('提供收据/发票'),
      SHOP_FREE_SHIPPING: t('包邮'),
      SHOP_DROPSHIPPING: t('一件代发'),
      SHOP_PICKUP_AT_HOME: t('上门自提'),
      SHOP_CUSTOMER_SERVICE_WORKING_TIME: t('人工客服工作时间'),
      ITEM_WARRANTY_SERVICE: t('保修服务'),
      ITEM_EXPIRATION_DATE: t('保质期/有效期'),
      OTHER_PRIVATE_SHIPPING: t('私密发货'),
      OTHER_GIFT: t('赠品'),
      SHOP_WHOLESALE: t('批发'),
    };
  }, [t]);

  return {
    t,
    groupLabel,
    knowledgeLabel,
  };
};

export const logisticsKnowledgeTypes: Knowledge.LogisticsKnowledgeType[] = [
  'LOGISTICS_SEND_OUT_TIME',
  'LOGISTICS_SEND_OUT_PLACE',
  'LOGISTICS_DELIVERED_IN_TIME',
  'SHOP_FREE_SHIPPING',
  'LOGISTICS_DELIVERY_COURIER_TYPE',
  'LOGISTICS_CONSOLIDATED_MULTIPLE_ORDER',
];

export const storeKnowledgeTypes: Knowledge.StoreKnowledgeType[] = [
  'LOGISTICS_SEND_PICTURES_BEFORE',
  'SHOP_INVOICE',
  'SHOP_DROPSHIPPING',
  'SHOP_PICKUP_AT_HOME',
  'ITEM_WARRANTY_SERVICE',
  'OTHER_GIFT',
  'SHOP_WHOLESALE',
];

const groupKnowledgesMap: Partial<Record<Knowledge.GroupType, Knowledge.KnowledgeType[]>> = {
  LOGISTICS: logisticsKnowledgeTypes,
  STORE: storeKnowledgeTypes,
};

export function useLLM() {
  const { qaEnable, whiteListConfig: { LLM: _LLM, QA_AI_3, QA_NEW_KNOWLEDGE } = {} } =
    useOrgConfigStateValue();
  return qaEnable ? QA_AI_3 || QA_NEW_KNOWLEDGE : _LLM;
}

export const useGroup = () => {
  const LLM = useLLM();

  const { loading, list, fetchList } = useCrud({
    readList: useCallback(() => {
      const _groupKnowledgesMap = LLM
        ? groupKnowledgesMap
        : ({ LOGISTICS: ['LOGISTICS_SEND_OUT_TIME'] } as typeof groupKnowledgesMap);
      return groupList().then((list) => {
        const allKnowledgeDefinitions = list
          .map(({ knowledgeDefinitions }) => knowledgeDefinitions)
          .flat();
        return list.reduce<typeof list>((res, { knowledgeDefinitions, code, ...others }) => {
          if (_groupKnowledgesMap[code]) {
            res.push({
              ...others,
              code,
              knowledgeDefinitions:
                _groupKnowledgesMap?.[code]?.reduce<typeof knowledgeDefinitions>(
                  (res, knowledgeType) => {
                    const knowledgeDefinition = allKnowledgeDefinitions.find(
                      ({ code }) => code === knowledgeType,
                    );
                    if (knowledgeDefinition) {
                      res.push(knowledgeDefinition);
                    }
                    return res;
                  },
                  [],
                ) ?? [],
            });
          }
          return res;
        }, []);
      });
    }, [LLM]),
  });

  return {
    loading,
    list,
    fetchList,
  };
};
